import styled from "styled-components";
import { FC, MouseEventHandler, useState } from "react";

const commonWheelConfig = {
  // [x, y] position in the viewBox (centered)
  cx: "50%",
  cy: "50%",
  fill: "none",
};

type WheelProps = {
  testId?: string;
  onMouseOver?: MouseEventHandler;
  radius: number;
  wheelStrokeWidth: number;
};

/**
 * Whole circle, under the ProgressWheel.
 * Draws the gutter for the progress.
 */
export const BaseWheel: FC<WheelProps> = ({
  onMouseOver,
  radius,
  wheelStrokeWidth,
  testId,
}) => (
  <circle
    {...commonWheelConfig}
    data-testid={testId ?? "base-wheel"}
    onMouseOver={onMouseOver}
    r={radius}
    stroke="var(--color-primary-100)"
    strokeWidth={wheelStrokeWidth}
  />
);

type AnimatedCircleProps = {
  $dashArray: string;
};

const AnimatedCircle = styled.circle<AnimatedCircleProps>`
  stroke-dasharray: ${({ $dashArray }) => $dashArray};

  @keyframes drawProgress {
    from {
      stroke-dasharray: 0, 100;
    }
  }

  &.showAnimation {
    animation: drawProgress
      ${({ $dashArray }) => {
        const percent = Number($dashArray.split(",")[1]);
        return percent >= 0 && percent <= 33
          ? "200ms"
          : percent > 33 && percent <= 66
            ? "400ms"
            : "600ms";
      }}
      cubic-bezier(0.64, 0.07, 0.83, 0.67);
  }
`;

const GraphColors = {
  ERROR: "var(--color-error-600)",
  WARNING: "var(--color-warning-600)",
  SUCCESS: "var(--color-success-600)",
};

const getProgressWheelColor = (percent: number) =>
  percent >= 0 && percent <= 50
    ? GraphColors.ERROR
    : percent > 50 && percent <= 90
      ? GraphColors.WARNING
      : GraphColors.SUCCESS;

/**
 * Calculates a dashArray (pattern of dashes and gaps) such as:
 * - Has a single visible dash, the rest is a gap
 * - Said dash describes a circular line of radius `radius`
 * - Only `percent`% of the circular line will be drawn
 *
 * The idea behind this function is known as the "dashed-stroke technique".
 * Info about it: https://jakearchibald.com/2013/animated-line-drawing-svg/
 *
 * @param {number} radius The radius of the output circular line
 * @param {number} percent Number between `0` and `100`, represents the
 * percentage of the output circular line that will be drawn
 * @return {string} Dasharray describing the output circular line
 */
const calculateDashArray = (radius: number, percent: number) => {
  const wheelCircumferenceLength = Math.PI * 2 * radius;
  const MINIMUM_PATH_LENGTH = 0.5;

  let pathLength = (wheelCircumferenceLength * percent) / 100;

  // The path should always be visible (According to the designs)
  if (pathLength < MINIMUM_PATH_LENGTH) {
    pathLength = MINIMUM_PATH_LENGTH;
  }

  return `${Math.round(pathLength)},100`;
};

type ProgressWheelProps = WheelProps & {
  percent: number;
  viewBoxSize: number;
};

export enum GraphTestIds {
  BaseWheel = "base-wheel",
  ProgressWheel = "progress-wheel",
}

/**
 * Partial circle, on top of the BaseWheel.
 * Draws the overlapping portion that represents the percent complete.
 */
export const ProgressWheel: FC<ProgressWheelProps> = ({
  onMouseOver,
  percent,
  radius,
  testId,
  wheelStrokeWidth,
  viewBoxSize,
}) => {
  const [showAnimation, setShowAnimation] = useState(true);

  /**
   * We apply a transformation to the ProgressWheel's circle because, in the design,
   * the progress line doesn't start from the middle (angle = 0), but from about 26º further
   *
   * The rotation point [x, y] is the center of the viewBox, which is the center of the circles
   * that describe the wheels
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/transform#rotate
   */
  const angle = 26;
  const [x, y] = [viewBoxSize / 2, viewBoxSize / 2];

  return (
    <AnimatedCircle
      {...commonWheelConfig}
      $dashArray={calculateDashArray(radius, percent)}
      className={showAnimation ? "showAnimation" : ""}
      data-testid={testId ?? "progress-wheel"}
      onAnimationEnd={() => setShowAnimation(false)} // Play animation just once
      onMouseOver={onMouseOver}
      r={radius}
      stroke={getProgressWheelColor(percent)}
      strokeLinecap="round"
      strokeWidth={wheelStrokeWidth}
      transform={`rotate( ${angle}, ${x}, ${y} )`}
    />
  );
};
